import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { catchError, EMPTY, Observable } from 'rxjs';

import { EnvironmentName } from '@blaze-shared/api-contracts-domain';
import { ProjectContextFacade } from '@blaze-shared/app-state-data-access';
import { AuthService } from '@blaze-shared/auth-data-access';
import {
  API_BASE_URL,
  DEV_SUBDOMAIN_OVERRIDE,
  FORCE_ENVIRONMENT_NAME_PARAM_NAME,
  NotificationService,
} from '@blaze-shared/platform-data-access';
import { urlUtils } from '@blaze-shared/platform-util';

@Injectable()
export class AppInterceptor implements HttpInterceptor {
  private readonly authService = inject(AuthService);
  private readonly apiBaseUrl = inject(API_BASE_URL);
  private readonly devSubdomainOverride = inject(DEV_SUBDOMAIN_OVERRIDE);
  private readonly notificationService = inject(NotificationService);
  private readonly projectContextFacade = inject(ProjectContextFacade);
  private readonly route = inject(ActivatedRoute);

  private isRedirectingOn403 = false;

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const isBlazeBackendRequest = request.url.startsWith(this.apiBaseUrl);
    const token = this.authService.authBearerToken;
    let modifiedRequest;
    let headers = request.headers;
    let params = request.params;

    if (isBlazeBackendRequest) {
      let defaultEnv: EnvironmentName = 'Development';

      const redirectPathnameFromUrl = decodeURI(this.route.snapshot.queryParams['redirect'] ?? '');
      if (
        urlUtils.isPublishedMode(redirectPathnameFromUrl) &&
        !urlUtils.isPublishedStagingMode(this.devSubdomainOverride, redirectPathnameFromUrl)
      ) {
        defaultEnv = 'Production';
      }

      let environmentName: EnvironmentName =
        this.projectContextFacade.environmentName ?? defaultEnv;

      if (params.has(FORCE_ENVIRONMENT_NAME_PARAM_NAME)) {
        environmentName =
          (params.get(FORCE_ENVIRONMENT_NAME_PARAM_NAME) as EnvironmentName | undefined) ??
          defaultEnv;
        params = params.delete(FORCE_ENVIRONMENT_NAME_PARAM_NAME);
      }

      headers = headers.append('environment', environmentName);

      // This ensures that none of the api calls are intercepted by the service worker.
      headers = headers.append('ngsw-bypass', 'true');
    }

    if (isBlazeBackendRequest && token) {
      // Public app URLs require the authorization token to be passed as the Bearer token
      headers = headers.append('Authorization', `Bearer ${this.authService.authBearerToken}`);
      modifiedRequest = request.clone({ withCredentials: true, headers, params });
    } else if (isBlazeBackendRequest && !token) {
      modifiedRequest = request.clone({ withCredentials: true, headers, params });
    } else {
      modifiedRequest = request.clone({ params });
    }

    const isCurrUrlPublic = urlUtils.isPublicUrl(globalThis.location.href);

    return next.handle(modifiedRequest).pipe(
      catchError((err: HttpErrorResponse) => {
        const pathname = globalThis.location.pathname;
        const url = new URL(globalThis.location.href);
        const searchParams: URLSearchParams = url.searchParams;
        if (pathname.includes('/featureflags')) {
          // This can fire even on pages where we don't want to redirect. As such, we ignore error and don't redirect.
          // If the access token is invalid, some other api call is sure to fail and cause the redirect.
          return EMPTY;
        }
        if (err.status === 404) {
          console.error('404:', err);
          if (!isCurrUrlPublic && err.error?.errorMessage === 'Project not found.') {
            console.log(`Redirecting to /projects`);
            globalThis.location.href = '/projects';
          }
        } else if (
          !this.isRedirectingOn403 &&
          !isCurrUrlPublic &&
          err.status === 403 &&
          (err.error?.errorCode === 'InvalidAccessToken' ||
            err.error?.errorCode === 'NoAccessToken')
        ) {
          // Prevent multiple endpoints which simultaneously return 403 from triggering redirect all at once.
          this.isRedirectingOn403 = true;
          setTimeout(() => {
            this.isRedirectingOn403 = false;
          }, 500);
          const shouldRedirect =
            pathname.includes('/app/') ||
            pathname.includes('/projects/') ||
            pathname.includes('/preview/');
          const redirectUrl = shouldRedirect ? pathname : undefined;
          this.authService.clearCacheAndRedirect(redirectUrl, searchParams);
        } else if (
          urlUtils.isLoginUrl() ||
          err.error?.errorCode === 'InvalidCredentials' ||
          (err.error?.errorCode !== 'AccessDenied' && err.error?.errorCode !== 'Forbidden')
        ) {
          throw err;
        } else {
          this.notificationService.error(`${err?.error?.errorMessage}`);
        }
        return EMPTY;
      }),
    );
  }
}
